/********************************************************************
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this
 * file in accordance with the terms of the Adobe license agreement
 * accompanying it.
 *******************************************************************/

import { fetchGraphQl } from '@/quote-management/api';
import { NEGOTIABLE_QUOTES_QUERY } from './graphql/NegotiableQuotesQuery';
import { transformNegotiableQuotesList } from '@/quote-management/data/transforms';
import { NegotiableQuotesListModel } from '@/quote-management/data/models/negotiable-quote-model';
import { state } from '@/quote-management/lib/state';

export enum FilterMatchTypeEnum {
  FULL = 'FULL',
  PARTIAL = 'PARTIAL',
}

export interface FilterEqualTypeInput {
  eq?: string;
  in?: string[];
}

export interface FilterMatchTypeInput {
  match?: string;
  match_type?: FilterMatchTypeEnum;
}

export interface NegotiableQuoteFilterInput {
  ids?: FilterEqualTypeInput;
  name?: FilterMatchTypeInput;
}

export enum SortEnum {
  ASC = 'ASC',
  DESC = 'DESC',
}

export enum NegotiableQuoteSortableField {
  QUOTE_NAME = 'QUOTE_NAME',
  CREATED_AT = 'CREATED_AT',
  UPDATED_AT = 'UPDATED_AT',
}

export interface NegotiableQuoteSortInput {
  sort_direction: SortEnum;
  sort_field: NegotiableQuoteSortableField;
}

export interface NegotiableQuotesParams {
  filter?: NegotiableQuoteFilterInput;
  pageSize?: number;
  currentPage?: number;
  sort?: NegotiableQuoteSortInput;
}

export const negotiableQuotes = async (
  params: NegotiableQuotesParams = {}
): Promise<NegotiableQuotesListModel> => {
  if (!state.authenticated) {
    return Promise.reject(new Error('Unauthorized'));
  }

  const variables = {
    filter: params.filter || null,
    pageSize: params.pageSize || 20,
    currentPage: params.currentPage || 1,
    sort: params.sort || null,
  };

  try {
    const results: any = await fetchGraphQl(NEGOTIABLE_QUOTES_QUERY, {
      variables,
    });

    if (!results?.data?.negotiableQuotes) {
      throw new Error('No quotes data received');
    }

    const transformedData = transformNegotiableQuotesList(
      results.data.negotiableQuotes
    );

    if (!transformedData) {
      throw new Error('Failed to transform quotes data');
    }

    return transformedData;
  } catch (error) {
    return Promise.reject(error);
  }
};
